import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { takePromise } from '@y42/util/take-promise';
import { catchError, filter, throwError, timeout } from 'rxjs';
import { AbstractFeatureEntityState, AbstractFeatureUIModel } from '../models';
import { AbstractFeatureQueryEntity } from '../state';
import { AbstractFeatureService } from './abstract-feature.service';

export abstract class AbstractDetailViewGuard<
  State extends AbstractFeatureEntityState,
  UIModel extends AbstractFeatureUIModel = AbstractFeatureUIModel,
> implements CanActivate
{
  constructor(
    protected readonly router: Router,
    protected readonly service: AbstractFeatureService<State>,
    protected readonly query: AbstractFeatureQueryEntity<State, UIModel>,
  ) {}

  abstract readonly NAME: string;

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<true | UrlTree> {
    await takePromise(
      this.query.loading$.pipe(
        filter((loading) => !loading),
        timeout(15000),
        catchError(() => throwError(() => `[${this.NAME}]: timeout occurred while waiting for query.loading$`)),
      ),
    );

    const $name = route.params.id;

    if (!$name) {
      this.service.setActive(null);
      return true;
    }

    const entity = this.query.getEntityByName($name);

    this.service.setActive(entity?.$path || null);

    if (!entity) {
      const parentUrl = this.getParentUrl(state.url);

      console.warn(`[${this.NAME}]: item "${$name}" not found, redirecting to "${parentUrl}"`);
      return parentUrl;
    }

    return true;
  }

  protected getParentUrl(url: string) {
    return this.router.parseUrl(url.split('/').slice(0, -2).join('/'));
  }
}
